// WCAG 2.x contrast helpers — pure, no DOM. Used by the .tap design editor to
// warn when an accent/text color is hard to read against the chosen background.

/** Parse a #rgb or #rrggbb hex string to [r,g,b] (0-255). Returns null if invalid. */
export function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) {
    h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  }
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Relative luminance per WCAG 2.x (sRGB). Input channels 0-255. */
export function relativeLuminance([r, g, b]: [number, number, number]): number {
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/**
 * WCAG contrast ratio between two hex colors (1–21). Returns 1 when either
 * color can't be parsed (treat unknown as "no contrast info", non-blocking).
 */
export function contrastRatio(hex1: string, hex2: string): number {
  const a = hexToRgb(hex1);
  const b = hexToRgb(hex2);
  if (!a || !b) return 1;
  const l1 = relativeLuminance(a);
  const l2 = relativeLuminance(b);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/** True when fg/bg contrast is below the threshold (default WCAG AA body, 4.5). */
export function isLowContrast(fg: string, bg: string, threshold = 4.5): boolean {
  return contrastRatio(fg, bg) < threshold;
}

/** True when the color is light (luminance > 0.5). Unparseable → false (dark). */
export function isLightColor(hex: string): boolean {
  const rgb = hexToRgb(hex);
  return rgb ? relativeLuminance(rgb) > 0.5 : false;
}

/**
 * Readable text color for a background: near-black or white, whichever actually
 * has more contrast (not a luminance guess — mid-tones like green read better
 * on black even when their luminance says "dark").
 */
export function readableTextColor(bg: string): string {
  const dark = "#0f172a";
  const light = "#ffffff";
  return contrastRatio(dark, bg) >= contrastRatio(light, bg) ? dark : light;
}
